import React from "react";
import { Modal, Button, Typography, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Text from "../../context/language-context";
import styles from "./fullscreen-modal.module.css";

const { Title } = Typography;

export interface FullscreenModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal is closed */
  onClose: () => void;
  /** Modal title - defaults to "Preview" */
  title?: React.ReactNode;
  /** Custom title element (overrides title prop) */
  customTitle?: React.ReactNode;
  /** Content to render in the modal body */
  children: React.ReactNode;
  /** Additional footer buttons to render before Close button */
  extraFooterButtons?: React.ReactNode;
  /** Completely custom footer (overrides default footer and extraFooterButtons) */
  customFooter?: React.ReactNode;
  /** Whether the modal is loading */
  loading?: boolean;
  /** Custom close button text */
  closeButtonText?: React.ReactNode;
  /** Whether to destroy modal on close */
  destroyOnClose?: boolean;
  /** Custom className for modal */
  className?: string;
}

/**
 * A reusable fullscreen modal component for previewing forms and content.
 * Provides consistent UI across dashboard, my-applications, and admin dashboard.
 *
 * @example
 * ```tsx
 * // Simple usage
 * <FullscreenModal open={!!previewData} onClose={() => setPreviewData(null)}>
 *   <FormPreviewContent />
 * </FullscreenModal>
 *
 * // With custom footer (for admin dashboard)
 * <FullscreenModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   customTitle={<CustomTitleWithStatus />}
 *   customFooter={<AdminActionButtons />}
 * >
 *   <AdminContent />
 * </FullscreenModal>
 * ```
 */
const FullscreenModal: React.FC<FullscreenModalProps> = ({
  open,
  onClose,
  title,
  customTitle,
  children,
  extraFooterButtons,
  customFooter,
  loading = false,
  closeButtonText,
  destroyOnClose = true,
  className,
}) => {
  const defaultTitle = Text({ tid: "PREVIEW", def: "Preview" });
  const defaultCloseText = Text({ tid: "CLOSE", def: "Close" });

  // Render default footer or custom footer
  const renderFooter = () => {
    if (customFooter) {
      return customFooter;
    }

    return (
      <div className={styles.modalFooter}>
        {extraFooterButtons}
        <Button
          type='primary'
          onClick={onClose}
          disabled={loading}
        >
          {loading ? (
            <Spin
              indicator={<LoadingOutlined spin />}
              size='small'
            />
          ) : (
            closeButtonText || defaultCloseText
          )}
        </Button>
      </div>
    );
  };

  // Render default title or custom title
  const renderTitle = () => {
    if (customTitle) {
      return customTitle;
    }

    return (
      <div className={styles.modalHeader}>
        <Title
          level={3}
          className={styles.modalTitle}
        >
          {title || defaultTitle}
        </Title>
      </div>
    );
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      destroyOnClose={destroyOnClose}
      className={`${styles.fullscreenModal} ${className || ""} previewModal`}
      width='100vw'
      title={renderTitle()}
      footer={renderFooter()}
    >
      <div className={styles.modalContent}>{children}</div>
    </Modal>
  );
};

export default FullscreenModal;
